import { useLocation } from 'react-router';

/**
 * Where a visitor was when they tapped «Войти» / «Создать аккаунт».
 *
 * Handed to the auth routes as react-router `state` (`<Link state={…}>`) and
 * read back there as `location.state.from`, which `postAuthPath` prefers over
 * everything but a pending team invitation. Without it, a person who registered
 * FROM a tender was landed on `/plans` or on the catalogue, and the lot they
 * had open — the reason they signed up — was gone.
 *
 * `state`, not `?next=`: the destination never touches the address bar, so it
 * cannot be forged by a link someone sends, cannot be logged, and cannot end up
 * in a pixel's `$current_url`. `postAuthPath` still validates it
 * (`isSafeNextPath`) — history state is caller-supplied data like any other,
 * and this app has been bitten before by treating "we wrote it" as "it is
 * safe". `?next=` remains supported on the reading side for links minted
 * outside the SPA (a letter, the bot).
 *
 * The hash is deliberately dropped: on `/login` the hash is where the e-mail
 * code lives (see `readLoginPrefill`), and no other page needs one carried
 * through a sign-in.
 */
export function useAuthOrigin(): { from: string } {
  const { pathname, search } = useLocation();
  return { from: `${pathname}${search}` };
}

/**
 * The destination `/login` and `/register` were asked to return to, if any.
 *
 * Two carriers, because they have different reaches:
 *  - react-router `state.from` — what every in-app entry point sets
 *    ({@link useAuthOrigin}). Never in the URL, so never logged or pixel-read.
 *  - `?next=` — for links minted OUTSIDE the SPA (a letter, the Telegram bot),
 *    which have no way to push history state.
 *
 * State wins: it is the one the app itself wrote a moment ago. Neither is
 * trusted — `postAuthPath` runs both through `isSafeNextPath`.
 */
export function usePostAuthNext(): string | null {
  const { state, search } = useLocation();
  const from = (state as { from?: unknown } | null)?.from;
  if (typeof from === 'string' && from !== '') return from;
  return new URLSearchParams(search).get('next');
}
